import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useCart } from "@/context/CartContext";
import { getProduct, money, PRODUCTS } from "@/lib/products";
import { MaskLine } from "@/components/lumi/Reveal";
import { useStock } from "@/context/StockContext";
import { NotifyMe } from "@/components/lumi/NotifyMe";

const Details = ({ product }) => (
  <Accordion type="single" collapsible className="mt-10 border-t border-[#E6D7CC]" data-testid="product-details">
    {[["ritual", "How to wear"], ["formula", "Ingredients"], ["shipping", "Shipping"]].map(([k, label]) => (
      <AccordionItem key={k} value={k} className="border-[#E6D7CC]">
        <AccordionTrigger className="font-display text-lg hover:no-underline" data-testid={`detail-${k}`}>{label}</AccordionTrigger>
        <AccordionContent className="text-[#6E504A] leading-relaxed">{product.details[k]}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export default function ProductPage() {
  const { id } = useParams();
  const product = getProduct(id);
  const [shade, setShade] = useState(product?.shades[0].name);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const { isSoldOut } = useStock();
  if (!product) return <Navigate to="/" replace />;
  const active = product.shades.find((s) => s.name === shade) ?? product.shades[0];
  const out = isSoldOut(product.id, active.name);
  const others = PRODUCTS.filter((p) => p.id !== product.id);

  return (
    <main className="pt-[100px] pb-24 bg-[#FAF7F2]" data-testid="product-page">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#6E504A] hover:text-[#281715] transition-colors" data-testid="product-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to collection
        </Link>
        <div className="mt-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6 lg:sticky lg:top-[100px]">
            <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
              className="relative aspect-[4/5] arch overflow-hidden bg-[#F4EFE6]">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
              <motion.span key={active.name} initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute bottom-6 left-6 w-14 h-14 rounded-full ring-4 ring-[#FAF7F2]" style={{ background: active.hex }} />
            </motion.div>
          </div>
          <div className="lg:col-span-6">
            <p className="overline text-[#6E504A]">{product.kind}</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.95]" data-testid="product-title">
              <MaskLine delay={0.1}>{product.name}</MaskLine>
            </h1>
            <p className="font-display italic text-2xl text-[#D88B94] mt-4">{product.tagline}</p>
            <div className="flex items-baseline gap-3 mt-6">
              <span className="font-display text-3xl" data-testid="product-page-price">{money(product.price)}</span>
              {product.compareAt && <span className="text-[#6E504A] line-through">{money(product.compareAt)} if bought separately</span>}
            </div>
            <p className="mt-6 text-[#6E504A] leading-relaxed max-w-lg">{product.description}</p>
            {["glasshouse-gloss", "melt-lip-oil"].includes(product.id) && (
              <p className="mt-6 inline-flex rounded-full bg-[#F9ECE8] px-4 py-2 text-sm text-[#4A2E2B]" data-testid="pdp-bundle-note">
                Launch bundle: buy 2 glosses, get 50% off a lip oil — applied in your bag
              </p>
            )}
            <div className="mt-10">
              <p className="text-sm"><span className="text-[#6E504A]">Shade —</span> <span className="font-medium" data-testid="selected-shade">{active.name}</span></p>
              <p className="text-sm text-[#6E504A] mt-1">{active.note}</p>
              <div className="mt-4 flex flex-wrap gap-3 text-[#281715]">
                {product.shades.map((s) => (
                  <button key={s.name} onClick={() => setShade(s.name)} data-testid={`pdp-shade-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-2 rounded-full pl-1.5 pr-4 h-11 border text-sm transition-colors duration-200 ${s.name === active.name ? "border-[#281715] bg-[#281715] text-[#FAF7F2]" : "border-[#E6D7CC] hover:border-[#4A2E2B]"}`}>
                    <span className="w-8 h-8 rounded-full" style={{ background: s.hex }} />{s.name}{isSoldOut(product.id, s.name) && <span className="text-[11px] uppercase tracking-wider opacity-70">· sold out</span>}
                  </button>
                ))}
              </div>
            </div>
            {out ? <div className="mt-10 max-w-lg"><NotifyMe key={active.name} productId={product.id} shade={active.name} /></div> : (
            <div className="mt-10 flex gap-3">
              <div className="flex items-center rounded-full border border-[#E6D7CC] h-14">
                <button className="w-12 h-full grid place-items-center" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="pdp-qty-dec" aria-label="Decrease"><Minus className="w-4 h-4" /></button>
                <span className="w-6 text-center" data-testid="pdp-qty">{qty}</span>
                <button className="w-12 h-full grid place-items-center" onClick={() => setQty((q) => Math.min(20, q + 1))} data-testid="pdp-qty-inc" aria-label="Increase"><Plus className="w-4 h-4" /></button>
              </div>
              <motion.button whileTap={{ scale: 0.97 }} onClick={() => { add(product.id, active.name, qty); toast.success(`${product.name} · ${active.name} added`); }}
                data-testid="pdp-add-to-bag" className="flex-1 h-14 rounded-full bg-[#281715] text-[#FAF7F2] hover:bg-[#4A2E2B] transition-colors duration-200">
                Add to bag — {money(product.price * qty)}
              </motion.button>
            </div>
            )}
            <Details product={product} />
          </div>
        </div>
        <div className="mt-28">
          <p className="overline text-[#6E504A]">Complete the look</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            {others.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} data-testid={`related-${p.id}`} className="group flex items-center gap-6 rounded-[28px] bg-[#F4EFE6] p-4 pr-8">
                <img src={p.image} alt={p.name} className="w-24 h-28 object-cover rounded-t-full rounded-b-2xl" />
                <div className="flex-1"><p className="font-display text-2xl group-hover:italic transition-all">{p.name}</p><p className="text-sm text-[#6E504A]">{money(p.price)}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
